// components/common/Conversations.js
import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Search, User, Clock } from 'react-feather';
import AuthService from '../../services/authService';
import BuyerDetailsModal from './BuyerDetailsModal';

const Conversations = ({ theme = 'light', onNavigateToUserManagement }) => {
  const [conversations, setConversations] = useState([]);
  const [assignedBuyers, setAssignedBuyers] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL || 'https://back-end-x7tx.onrender.com/api';

  // Theme styles
  const isDark = theme === 'dark';
  const styles = {
    container: {
      display: 'flex',
      height: 'calc(100vh - 80px)',
      backgroundColor: isDark ? '#0f1419' : '#ffffff',
      borderRadius: isDark ? '12px' : '8px',
      overflow: 'hidden',
      border: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
      boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    sidebar: {
      width: '350px',
      borderRight: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDark ? 'rgba(15, 20, 25, 0.8)' : '#f9fafb'
    },
    header: {
      padding: '20px',
      borderBottom: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
      backgroundColor: isDark ? 'rgba(15, 20, 25, 0.8)' : '#ffffff'
    },
    title: {
      margin: '0 0 16px 0',
      fontSize: '1.5rem',
      fontWeight: '700',
      color: isDark ? '#ffffff' : '#111827',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    searchInput: {
      width: '100%',
      padding: '10px 12px 10px 40px',
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
      border: isDark ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid #d1d5db',
      borderRadius: '8px',
      color: isDark ? '#ffffff' : '#111827',
      fontSize: '0.9rem',
      outline: 'none'
    },
    conversationItem: (isSelected) => ({
      padding: '16px 20px',
      borderBottom: isDark ? '1px solid rgba(99, 102, 241, 0.1)' : '1px solid #e5e7eb',
      cursor: 'pointer',
      backgroundColor: isSelected 
        ? (isDark ? 'rgba(99, 102, 241, 0.1)' : '#f3f4f6')
        : (isDark ? 'transparent' : '#ffffff'),
      transition: 'background-color 0.2s'
    }),
    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      border: isDark ? '2px solid rgba(99, 102, 241, 0.3)' : '2px solid #c7d2fe'
    },
    userName: (hasUnread) => ({
      fontWeight: hasUnread ? '700' : '600',
      color: isDark ? '#ffffff' : '#111827',
      fontSize: '0.95rem'
    }),
    lastMessage: {
      fontSize: '0.85rem',
      color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginBottom: '4px',
      textAlign: 'left'
    },
    timestamp: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.75rem',
      color: isDark ? 'rgba(255, 255, 255, 0.4)' : '#9ca3af'
    },
    messageArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDark ? '#0f1419' : '#ffffff'
    },
    chatHeader: {
      padding: '20px',
      borderBottom: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
      backgroundColor: isDark ? 'rgba(15, 20, 25, 0.8)' : '#f9fafb'
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      backgroundColor: isDark ? '#0f1419' : '#f9fafb'
    },
    messageBubble: (isSender) => ({
      maxWidth: '70%',
      padding: '12px 16px',
      borderRadius: '16px',
      backgroundColor: isSender 
        ? (isDark ? 'rgba(99, 102, 241, 0.2)' : '#6366f1')
        : (isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'),
      border: isSender 
        ? (isDark ? '1px solid rgba(99, 102, 241, 0.3)' : 'none')
        : (isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb'),
      color: isSender 
        ? (isDark ? '#ffffff' : '#ffffff')
        : (isDark ? '#ffffff' : '#111827'),
      boxShadow: isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
      textAlign: 'left'
    }),
    inputArea: {
      padding: '20px',
      borderTop: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
      backgroundColor: isDark ? 'rgba(15, 20, 25, 0.8)' : '#ffffff'
    },
    textarea: {
      flex: 1,
      padding: '12px',
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
      border: isDark ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid #d1d5db',
      borderRadius: '8px',
      color: isDark ? '#ffffff' : '#111827',
      fontSize: '0.95rem',
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit'
    },
    sendButton: (disabled) => ({
      padding: '12px 24px',
      backgroundColor: disabled 
        ? (isDark ? 'rgba(99, 102, 241, 0.3)' : '#d1d5db')
        : '#6366f1',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: '600',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'background-color 0.2s'
    })
  };

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data);
        
        // Fetch assigned buyers to find ones without active conversations (with messages)
        const currentUser = AuthService.getCurrentUser();
        if (currentUser?.role === 'agent') {
          const buyersResponse = await fetch(`${API_URL}/messages/assigned-buyers`, {
            headers: {
              'Authorization': `Bearer ${AuthService.getToken()}`
            }
          });
          
          const buyersData = await buyersResponse.json();
          if (buyersData.success) {
            // Get user IDs from conversations that have messages
            const conversationsWithMessages = data.data.filter(c => c.lastMessage?.content);
            const activeConversationUserIds = conversationsWithMessages.map(c => c.user._id);
            
            // Show buyers who either have no conversation or have an empty conversation
            const buyersWithoutActiveConvos = buyersData.data.filter(
              buyer => !activeConversationUserIds.includes(buyer._id)
            );
            
            setAssignedBuyers(buyersWithoutActiveConvos);
          }
        }
        
        // If a conversation is selected, refresh its messages
        if (selectedConversation) {
          const updated = data.data.find(c => c._id === selectedConversation._id);
          if (updated) {
            loadConversation(updated._id);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages || []);
        
        // Mark as read
        await fetch(`${API_URL}/messages/conversations/${conversationId}/read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`
          }
        });
        
        // Update conversation in list to reset unread count
        setConversations(prev => prev.map(c => 
          c._id === conversationId ? { ...c, unreadCount: 0 } : c
        ));
        
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
    }
  };

  const startNewConversation = async (buyer) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: buyer._id,
          subject: `Message from ${AuthService.getCurrentUser()?.name || 'Agent'}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Add new conversation to list and select it
        setConversations(prev => [data.data, ...prev]);
        // Remove from assigned buyers list
        setAssignedBuyers(prev => prev.filter(b => b._id !== buyer._id));
        // Load the new conversation
        loadConversation(data.data._id);
      } else {
        setError(data.error || 'Failed to create conversation');
      }
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError('Failed to start conversation');
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${selectedConversation._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          content: messageText.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages([...messages, data.data]);
        setMessageText('');
        setTimeout(scrollToBottom, 100);
        
        // Refresh conversations list to update lastMessage
        fetchConversations();
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Error sending message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredConversations = conversations.filter(conv => {
    // Only show conversations that have messages (lastMessage exists)
    if (!conv.lastMessage?.content) return false;
    
    // Apply search filter
    const userName = conv.user?.name?.toLowerCase() || '';
    const userEmail = conv.user?.email?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return userName.includes(search) || userEmail.includes(search);
  });


  const formatTime = (date) => {
    if (!date) return '';
    const messageDate = new Date(date);
    const now = new Date();
    const diffMs = now - messageDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return messageDate.toLocaleDateString();
  };

  const formatMessageTime = (date) => {
    const messageDate = new Date(date);
    return messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const currentUserRole = AuthService.getCurrentUser()?.role;

  return (
    <div style={styles.container}>
      {/* Conversations List */}
      <div style={styles.sidebar}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            <MessageCircle size={24} style={{ color: '#818cf8' }} />
            Conversations
          </h2>
          
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search 
              size={18} 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af'
              }}
            />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* Conversations List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#6b7280'
            }}>
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#6b7280'
            }}>
              {searchTerm ? 'No conversations found' : 'No conversations yet'}
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv._id}
                onClick={() => loadConversation(conv._id)}
                style={styles.conversationItem(selectedConversation?._id === conv._id)}
                onMouseEnter={(e) => {
                  if (selectedConversation?._id !== conv._id) {
                    e.currentTarget.style.backgroundColor = isDark 
                      ? 'rgba(99, 102, 241, 0.05)' 
                      : '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedConversation?._id !== conv._id) {
                    e.currentTarget.style.backgroundColor = isDark 
                      ? 'transparent' 
                      : '#ffffff';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  {/* Avatar */}
                  <div style={styles.avatar}>
                    {conv.user?.profileImage && conv.user.profileImage !== 'default-profile.jpg' ? (
                      <img 
                        src={conv.user.profileImage} 
                        alt={conv.user.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(99, 102, 241, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <User size={24} style={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : '#6366f1' }} />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}>
                      <span style={styles.userName(conv.unreadCount > 0)}>
                        {conv.user?.name || 'Unknown User'}
                      </span>
                      {conv.unreadCount > 0 && (
                        <span ref={(el) => {
                          if (el) {
                            el.style.setProperty('color', '#ffffff', 'important');
                            el.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
                          }
                        }} style={{
                          backgroundColor: '#1a1a1a',
                          color: '#ffffff',
                          fontSize: '0.6rem',
                          fontWeight: '700',
                          padding: '0 6px',
                          borderRadius: '8px',
                          minWidth: '16px',
                          height: '16px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid rgba(255, 255, 255, 0.15)'
                        }}>
                          {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                        </span>
                      )}
                    </div>
                    
                    <div style={styles.lastMessage}>
                      {conv.lastMessage?.content || 'No messages yet'}
                    </div>
                    
                    <div style={styles.timestamp}>
                      <Clock size={12} />
                      {formatTime(conv.lastMessage?.timestamp || conv.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Start New Conversation Section */}
          {!isLoading && assignedBuyers.length > 0 && (
            <>
              <div style={{
                padding: '16px 20px',
                borderTop: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
                borderBottom: isDark ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #e5e7eb',
                backgroundColor: isDark ? 'rgba(99, 102, 241, 0.05)' : '#f9fafb',
                fontSize: '0.75rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#6b7280'
              }}>
                Start New Conversation
              </div>
              
              {assignedBuyers.map(buyer => (
                <div
                  key={buyer._id}
                  onClick={() => startNewConversation(buyer)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: isDark ? '1px solid rgba(99, 102, 241, 0.1)' : '1px solid #e5e7eb',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDark 
                      ? 'rgba(99, 102, 241, 0.05)' 
                      : '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    {/* Avatar */}
                    <div style={styles.avatar}>
                      {buyer.profileImage && buyer.profileImage !== 'default-profile.jpg' ? (
                        <img 
                          src={buyer.profileImage} 
                          alt={buyer.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            // On error, replace with fallback
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `
                              <div style="
                                width: 100%;
                                height: 100%;
                                border-radius: 50%;
                                background-color: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(99, 102, 241, 0.1)'};
                                display: flex;
                                align-items: center;
                                justify-content: center;
                              ">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${isDark ? 'rgba(255, 255, 255, 0.3)' : '#6366f1'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(99, 102, 241, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <User size={24} style={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : '#6366f1' }} />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: '600',
                        color: isDark ? '#ffffff' : '#111827',
                        fontSize: '0.95rem',
                        marginBottom: '4px',
                        textAlign: 'left'
                      }}>
                        {buyer.name || 'Unknown User'}
                      </div>
                      
                      <div style={{
                        fontSize: '0.85rem',
                        color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af',
                        fontStyle: 'italic',
                        textAlign: 'left'
                      }}>
                        Click to start conversation
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div style={styles.messageArea}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div style={styles.chatHeader}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {/* Avatar */}
                <div style={styles.avatar}>
                  {selectedConversation.user?.profileImage && selectedConversation.user.profileImage !== 'default-profile.jpg' ? (
                    <img 
                      src={selectedConversation.user.profileImage} 
                      alt={selectedConversation.user.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(99, 102, 241, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={24} style={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : '#6366f1' }} />
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                  <div 
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: isDark ? '#ffffff' : '#111827',
                      cursor: 'pointer',
                      transition: 'color 0.2s',
                      textAlign: 'left'
                    }}
                    onClick={() => {
                      console.log('Opening buyer modal with data:', selectedConversation.user);
                      console.log('Onboarding data:', selectedConversation.user?.onboardingData);
                      console.log('Lead tier:', selectedConversation.user?.leadTier);
                      console.log('Likes:', selectedConversation.user?.likes);
                      console.log('Dislikes:', selectedConversation.user?.dislikes);
                      setSelectedBuyer(selectedConversation.user);
                      setShowBuyerModal(true);
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#6366f1';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = isDark ? '#ffffff' : '#111827';
                    }}
                  >
                    {selectedConversation.user?.name || 'Unknown User'}
                  </div>
                  {selectedConversation.user?.phone && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
                      textAlign: 'left'
                    }}>
                      {selectedConversation.user.phone}
                    </div>
                  )}
                  {selectedConversation.user?.email && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#6b7280',
                      textAlign: 'left'
                    }}>
                      {selectedConversation.user.email}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={messagesContainerRef} style={styles.messagesContainer}>
              {messages.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#6b7280',
                  padding: '40px 20px'
                }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((message, index) => {
                  const isSender = message.senderRole === currentUserRole;
                  const showDate = index === 0 || 
                    new Date(messages[index - 1].createdAt).toDateString() !== 
                    new Date(message.createdAt).toDateString();

                  return (
                    <React.Fragment key={message._id}>
                      {showDate && (
                        <div style={{
                          textAlign: 'center',
                          margin: '20px 0',
                          fontSize: '0.85rem',
                          color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#6b7280'
                        }}>
                          {new Date(message.createdAt).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      )}
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: isSender ? 'flex-end' : 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <div style={styles.messageBubble(isSender)}>
                          <div style={{
                            fontSize: '0.95rem',
                            lineHeight: '1.5',
                            wordWrap: 'break-word'
                          }}>
                            {message.content}
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            color: isSender 
                              ? (isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.7)')
                              : (isDark ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af'),
                            marginTop: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '4px'
                          }}>
                            {formatMessageTime(message.createdAt)}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div style={styles.inputArea}>
              {error && (
                <div style={{
                  marginBottom: '12px',
                  padding: '10px',
                  backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#fee2e2',
                  border: isDark ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #fca5a5',
                  borderRadius: '8px',
                  color: isDark ? '#fca5a5' : '#dc2626',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              )}
              
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end'
              }}>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  rows={3}
                  style={styles.textarea}
                />
                <button
                  onClick={sendMessage}
                  disabled={!messageText.trim() || isSending}
                  style={styles.sendButton(!messageText.trim() || isSending)}
                >
                  <Send size={18} />
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#6b7280',
            fontSize: '1.1rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <MessageCircle size={64} style={{ 
                color: isDark ? 'rgba(99, 102, 241, 0.3)' : '#d1d5db',
                marginBottom: '16px'
              }} />
              <div>Select a conversation to start messaging</div>
            </div>
          </div>
        )}
      </div>

      {/* Buyer Details Modal */}
      {showBuyerModal && selectedBuyer && (
        <BuyerDetailsModal
          buyer={selectedBuyer}
          isOpen={showBuyerModal}
          onClose={() => {
            setShowBuyerModal(false);
            setSelectedBuyer(null);
          }}
          onMessage={() => {
            setShowBuyerModal(false);
            setSelectedBuyer(null);
          }}
          onCurateMatch={() => {
            setShowBuyerModal(false);
            setSelectedBuyer(null);
            if (onNavigateToUserManagement) {
              onNavigateToUserManagement();
            }
          }}
        />
      )}
    </div>
  );
};

export default Conversations;